import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const SelectPackage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all packages on component mount
  useEffect(() => {
    fetchPackages();
  }, []);

  // Function to fetch packages from API
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://server.enjoywiki.com/youtubeai-may/packages");
      if (!response.ok) {
        throw new Error("Failed to fetch packages");
      }
      const data = await response.json();
      setPackages(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching packages:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get package features
  const getPackageFeatures = (pkg) => {
    const features = [];
    if (pkg.pointOne) features.push(pkg.pointOne);
    if (pkg.pointTwo) features.push(pkg.pointTwo);
    if (pkg.pointThree) features.push(pkg.pointThree);
    if (pkg.pointFour) features.push(pkg.pointFour);
    if (pkg.pointFive) features.push(pkg.pointFive);
    if (pkg.pointSix) features.push(pkg.pointSix);
    if (pkg.pointSeven) features.push(pkg.pointSeven);
    if (pkg.pointEight) features.push(pkg.pointEight);
    if (pkg.pointNine) features.push(pkg.pointNine);
    if (pkg.pointTen) features.push(pkg.pointTen);
    return features;
  };

  return (
    <>
      <section 
        className="pricing-section-5 section-padding position-relative" 
        style={{ 
          backgroundColor: '#0f0f1a',
          backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(225, 36, 36, 0.1) 0%, transparent 20%), radial-gradient(circle at 90% 80%, rgba(36, 225, 140, 0.1) 0%, transparent 20%)'
        }}
      >
        <div className="container">
          {/* Section Header */}
          <div className="section-header text-center mb-5">
            <p className="text-uppercase text-secondary fw-bold ls-2" style={{ letterSpacing: '2px', color: '#e12424' }}>
              Pricing Plan
            </p>
            <h2 className="text-white">Select Your Package</h2>
            <p className="text-secondary">Unlock powerful AI tools to grow your channel</p>
          </div>

          <div className="row justify-content-center">
            {loading && (
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-danger" role="status"></div>
                <p className="mt-3 text-white">Loading packages...</p>
              </div>
            )}

            {error && (
              <div className="col-12">
                <div className="alert alert-danger bg-transparent border-danger text-danger text-center" role="alert">
                  {error}
                </div>
              </div>
            )}

            {!loading && !error && (
              packages.map((pkg, index) => {
                // Highlight the middle package (index 1) as "Best Value"
                const isPopular = index === 1;
                
                return (
                  <div
                    key={pkg._id || index}
                    className="col-xl-4 col-lg-4 col-md-6 col-12 mb-4"
                  >
                    <div 
                      className="pricing-card h-100 position-relative overflow-hidden"
                      style={{
                        background: '#161625',
                        borderRadius: '15px',
                        border: isPopular ? '2px solid #e12424' : '1px solid rgba(255,255,255,0.05)',
                        transition: 'all 0.3s ease',
                        transform: isPopular ? 'scale(1.03)' : 'scale(1)',
                        zIndex: isPopular ? '2' : '1',
                        boxShadow: isPopular ? '0 10px 30px rgba(225, 36, 36, 0.15)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if(!isPopular) {
                           e.currentTarget.style.transform = 'translateY(-10px)';
                           e.currentTarget.style.borderColor = '#e12424';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if(!isPopular) {
                           e.currentTarget.style.transform = 'translateY(0)';
                           e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                        }
                      }}
                    >
                      {/* Popular Badge */}
                      {isPopular && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: '20px',
                            right: '-35px',
                            background: '#e12424',
                            color: '#fff',
                            padding: '5px 40px',
                            transform: 'rotate(45deg)',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            zIndex: '10',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
                          }}
                        >
                          POPULAR
                        </div>
                      )}

                      <div className="pricing-header text-center pt-5 pb-4">
                        <h3 className="text-white mb-2 fw-bold">{pkg.packageName || "Plan Name"}</h3>
                        <p className="text-secondary small mb-3">Perfect for creators</p>
                        <div className="price-amount position-relative d-inline-block">
                          <h1 className="text-white fw-bold mb-0" style={{ fontSize: '3.5rem', lineHeight: '1' }}>
                            ${pkg.price || 0}
                          </h1>
                          <span className="text-secondary" style={{ fontSize: '1rem' }}> USD</span>
                        </div>
                      </div>
                      
                      <div className="pricing-body px-4 pb-5">
                        <ul className="list-unstyled mb-4">
                          {getPackageFeatures(pkg).length > 0 ? (
                            getPackageFeatures(pkg).map((feature, featureIndex) => (
                              <li key={featureIndex} className="mb-3 d-flex align-items-start">
                                <div className="me-3 mt-1">
                                  <i className="icofont-check-circled" style={{ color: '#24d17a', fontSize: '1.2rem' }}></i>
                                </div>
                                <span className="text-secondary" style={{ fontSize: '0.95rem' }}>
                                  {feature}
                                </span>
                              </li>
                            ))
                          ) : (
                            <li className="text-center text-secondary">No features available</li>
                          )}
                        </ul>

                        <div className="pricing-btn w-100">
                          <Link 
                            to={`/buy/${pkg._id}`} 
                            className="btn d-block text-decoration-none text-white fw-bold py-3 rounded-pill"
                            style={{
                              background: isPopular 
                                ? 'linear-gradient(90deg, #e12424 0%, #ff7e5f 100%)' 
                                : 'transparent',
                              border: isPopular ? 'none' : '2px solid #e12424',
                              transition: 'all 0.3s ease',
                              boxShadow: isPopular ? '0 5px 15px rgba(225, 36, 36, 0.3)' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if(!isPopular) {
                                e.currentTarget.style.background = '#e12424';
                                e.currentTarget.style.color = '#fff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if(!isPopular) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            Get Started Now
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default SelectPackage;